<template>
	<v-form @submit.prevent :disabled="disableActions">
		<!-- fixed data -->
		<v-row dense align="center" class="ma-0 mb-2 px-1">
			<!-- itemId -->
			<v-col lg="3" md="6" sm="8" cols="12">
				<v-autocomplete
					v-model="itemId"
					:label="$T('inputs.item')"
					:items="cardamomItems"
					item-text="name"
					item-value="id"
					hide-details
					dense
					outlined
					:disabled="permissions.managingMaterialSimulation.isReadonly"
					@change="updateTableFields()"
					readonly
				></v-autocomplete>
			</v-col>

			<!-- tax percent -->
			<v-col lg="3" md="4" sm="6" cols="12">
				<v-text-field
					v-model="weightFormat"
					:label="$T('inputs.wanted-weight')"
					min="0"
					hide-spin-buttons
					outlined
					dense
					hide-details
					:disabled="permissions.managingMaterialSimulation.isReadonly"
					@input="updateTableFields()"
					v-number-format.number="[weightFormat, weight]"
				/>
			</v-col>

			<v-spacer />
			<v-col
				cols="auto"
				class="text-body-1 font-weight-bold"
				:class="[
					totalPercent === 100 ? 'primary--text' : 'red--text',
					{'shake': totalPercent !== 100}
				]"
			>
				{{totalPercent}}%
				<v-icon v-if="totalPercent !== 100" color="red">$alert</v-icon>
			</v-col>
			<v-spacer />
		</v-row>
		<!-- table -->
		<bee-handy-smart-table
			:headers="headers"
			:items="loading ? [] : entries"
			fixed-header
			:loading="loading"
			:height="$vuetify.breakpoint.height - 207"
			:items-per-page="-1"
			:data-table-props="{ disableSort: true }"
			:item-class="item => item.parentId ? 'grey lighten-4' : null"
			hot-inputs
			dense
			hide-inputs-details
			hide-default-footer
		>
			<!-- item name -->
			<template v-slot:item.itemName="{ item }">
				<span :class="{'ms-6': item.parentId}">
					{{item.itemName}}
				</span>
			</template>
			
			<!-- percent -->
			<template v-slot:item.percent="{ item, index }">
				<div class="d-flex align-center">
					<v-text-field
						v-model.number="item.percent"
						type="number"
						min="0"
						hide-spin-buttons
						dense
						hide-details
						class="mb-4"
						:class="{'ms-4': item.parentId}"
						:error="
							item.parentId
								? getTotalChildrenPercent(entries, item.parentId) !== 100
								: false
						"
						:rules="rules.requiredAsPercent"
						:disabled="permissions.managingMaterialSimulation.isReadonly"
						@input="updateTableFields(index, true);"
						@keydown.tab="
							item.id === entries[entries.length - 1].id
								? (() => { $event.preventDefault(); $refs.salePriceInput1.focus(); })()
								: null
						"
					/>
					<div
						v-if="item.hasChildren"
						class="text-caption ms-6"
						:class="{'alert-color--text': getTotalChildrenPercent(entries, item.id) !== 100}"
					>
						({{getTotalChildrenPercent(entries, item.id)}}%)
					</div>
				</div>
			</template>
		</bee-handy-smart-table>

		<!-- footer -->
		<v-footer app inset elevation="10" height="60" class="grey lighten-2">
			<v-progress-linear absolute top v-if="submitLoading" indeterminate/>
			
			{{$T('total-quantity')}}:

			<v-chip
				color="primary"
				class="ms-2 text-h6 font-weight-bold white--text"
			>
				{{ $options.filters.numberFormat((fixNumber(toQuintal(total)) || 0)) + ' qq' }}
			</v-chip>
			
			<v-spacer/>
			<v-btn
				:disabled="submitLoading || !permissions.managingMaterialSimulation.isReadWrite"
				color="primary"
				class="me-2"
				@click="submitSimulation()"
			>{{$T('save')}}</v-btn>
		</v-footer>
	</v-form>
</template>

<script>
import rules from '@/helpers/validation-rules'
import { fixNumber, toQuintal } from '@/helpers/functions'
import { mapState } from 'vuex'
import { type, itemCategory } from '@/helpers/enums'

export default {
	name: 'MaterialSimulation',

	data() {
		return {
			rules,
			type,

			loading: false,
			submitLoading: false,
			disableActions: false,

			weight: null,
			itemId: null,

			entries: [],
			total: 0,
			itemCategory,
			weightNumber: null
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			cardamomRootId: state => state.configs.itemsConfigs.cardamomRootId,
			items: state => state.items.items,
			itemsTree: state => state.items.itemsTree,
		}),

		headers() {
			return [
				{
					text: this.$T('headers.item'),
					name: 'itemName',
					value: 'itemName',
					columnClass: 'px-2',
					noNotAvailableText: true,
					width: 350
				},
				{
					text: '%',
					name: 'percent',
					value: 'percent',
					columnClass: 'px-2',
					type: 'number',
					noNotAvailableText: true,
					width: 120,
				},
				{
					text: this.$T('headers.weight') + ' (Kg)',
					name: 'weight',
					value: 'weight',
					type: 'number',
					noNotAvailableText: true,
					columnClass: 'text-center',
					cellClass: 'text-center',
					setValueExpr: val => val > 0 ? this.$options.filters.numberFormat(fixNumber(val)) : 0
				},
				{
					text: this.$T('headers.weight') + ' (qq)',
					name: 'weight',
					value: 'weight',
					type: 'number',
					noNotAvailableText: true,
					columnClass: 'text-center',
					cellClass: 'text-center',
					setValueExpr: val => val > 0 ? this.$options.filters.numberFormat(fixNumber(toQuintal(val))) : 0
				}
			]
		},
		totalPercent() {
			return this.entries.filter(c => !c.parentId).map(c => c.percent).reduce((a, b) => +a + b, 0)
		},
		cardamomItems() {
			return [this.itemsTree.find(c => c.id === this.cardamomRootId)];
		}

	},

	methods: {
		toQuintal,
		
		updateTableFields(index, updateFields = false) {
			const updateEntry = (index) => {
				// update weight for the item
				this.entries[index].weight = 
					(this.entries[index].percent / 100) * this.weight
				
				// update children if there is children
				if (this.entries[index].hasChildren) {
					for (let i = 0; i < this.entries.length; i++) {
						if (this.entries[index].id === this.entries[i].parentId) {
							this.entries[i].weight =
								(this.entries[i].percent / 100) * this.entries[index].weight;
						}
					}
				}

				if (this.entries[index].parentId) {
					this.entries[index].weight = this.entries.find(c => c.id === this.entries[index].parentId).weight * (this.entries[index].percent / 100)
				} else {
					this.entries[index].weight = this.weight * (this.entries[index].percent / 100)
				}

				this.total = this.entries.filter(c => !c.parentId).map(c => c.weight).reduce((a, b) => +a + b, 0);
			}

			const updateRows = () => {
				for (let i = 0; i < this.entries.length; i++) { updateEntry(i); }
			}

			if (index !== undefined) {
				updateEntry(index);
				if (updateFields) updateRows();
			} else {
				updateRows()
			}
		},

		updateItemsEntries() {
			this.entries = [];
			
			const subTree = this.itemsTree.find(c => c.id === this.cardamomRootId);

			subTree.children.filter(c => c.itemCategory === itemCategory.commercial).forEach(c => {
				this.entries.push({
					id: c.id,
					itemName: c.name,
					parentId: null,
					hasChildren: Boolean(c.children && c.children.length > 0),
					weight: 0,
					percent: 0,
					type: c.type
				})
				if (!c.children.length) return;

				c.children.forEach(e => {
					this.entries.push({
						id: e.id,
						itemName: e.name,
						parentId: c.id,
						hasChildren: false,
						weight: 0,
						percent: 0,
						type: c.type
					})
				})
			})
		},

		getTotalChildrenPercent(entries, parentId) {
			return entries.filter(c => c.parentId === parentId).reduce((res, a) => res + +a.percent, 0);
		},

		submitSimulation(isDraft = false) {
			this.submitLoading = !isDraft;
			setTimeout(() => {
				this.$eStorage.l.set('simulation-cardamom', {
					itemId: this.itemId,
					weight: this.weight,
					entries: this.entries.map(c => ({
						id: c.id,
						percent: c.percent
					}))
				});
				if (!isDraft) {
					this.$eventBus.$emit('show-snackbar', this.$T('messages.material-simulation-saved'));
					this.submitLoading = false;
				}
			}, !isDraft ? 1000 : 0);
		},

		fixNumber
	},

	created() {
		if (this.items.length) { this.updateItemsEntries(); }

		if (this.$eStorage.l.has('simulation-cardamom')) {
			const rawCardamomSimulation = this.$eStorage.l.get('simulation-cardamom');

			this.itemId = rawCardamomSimulation.itemId || this.cardamomItems[0];

			this.weightFormat = this.$options.filters.numberFormat(rawCardamomSimulation.weight);

			this.weight = rawCardamomSimulation.weight;
			
			rawCardamomSimulation.entries.forEach(c => {
				const index = this.entries.findIndex(e => e.id === c.id);
				if (index !== -1) { this.entries[index].percent = c.percent; }
			})
			this.updateTableFields();
		}
	},

	beforeDestroy() {
		this.submitSimulation(true);
	},
}
</script>

<style lang="scss">
.shake {
	position: relative;
	&::after {
		content: '';
		display: block;
		position: absolute;
		left: 0;
		bottom: 4px;
		right: 0;
		width: 80%;
		margin: 0 auto;
		height: 2px;
		background: #f44336;

		animation: shake 1s ease-in-out infinite;

		@keyframes shake {
			0% { width: 0; }
			50% { width: 80%; }
			100% { width: 0; }
		}
	}
}
</style>